import { mdiClose } from '@mdi/js';
import { Button, Dialog, DialogContent, DialogProps, DialogTitle, TextField } from '@mui/material';
import ButtonBase from '@mui/material/ButtonBase';
import Box from '@mui/system/Box';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers';
import { cs } from 'date-fns/locale';
import { FC, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Icon } from '@mdi/react';

import { defineStyles } from '../../theme';

export type PostponeCallFormData = {
  appointmentTime: Date;
  appointmentName: string;
  appointmentTelephoneNumber: string;
};

type PostponeCallFormValues = {
  appointmentTime: Date | null;
  appointmentName: string;
  appointmentTelephoneNumber: string;
};

const styles = defineStyles({
  root: { height: '100%' },
  header: { display: 'flex', justifyContent: 'space-between' },
  content: { display: 'flex', flexDirection: 'column', gap: 2 },
});

export type PostponeCallDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: PostponeCallFormData) => void | Promise<void>;
};

const defaultValues: PostponeCallFormValues = {
  appointmentTime: null,
  appointmentName: '',
  appointmentTelephoneNumber: '',
};

type PhoneValidationErrorCode =
  | 'INVALID_CHARACTERS'
  | 'CONTAINS_LETTERS'
  | 'MISSING_COUNTRY_PREFIX'
  | 'TOO_SHORT'
  | 'TOO_LONG';

function validatePhoneInput(raw: string): PhoneValidationErrorCode | null {
  const value = String(raw ?? '').trim();
  if (value === '') return null;

  // Letters (incl. diacritics) are always invalid in a phone number.
  if (/[A-Za-z\u00C0-\u024F\u1E00-\u1EFF]/.test(value)) return 'CONTAINS_LETTERS';

  // Allow digits, whitespace, separators, parentheses, and an optional leading "+".
  // Everything else is considered an invalid character.
  const hasInvalidChars = /[^0-9+\s().-]/.test(value);
  if (hasInvalidChars) return 'INVALID_CHARACTERS';

  const normalizedDigitsOnly = value.replace(/^\+/, '00').replace(/[^0-9]/g, '');

  // Local CZ number (9 digits) is accepted (BE can normalize to 00420...).
  if (/^\d{9}$/.test(normalizedDigitsOnly)) return null;

  // International form expected: 00 + 11..15 digits (same as BE).
  if (/^00[0-9]{11,15}$/.test(normalizedDigitsOnly)) return null;

  // Provide specific feedback.
  if (!normalizedDigitsOnly.startsWith('00')) return 'MISSING_COUNTRY_PREFIX';

  // Length checks for the 00-prefixed form.
  const len = normalizedDigitsOnly.length;
  if (len < 13) return 'TOO_SHORT'; // 00 + 11 digits
  if (len > 17) return 'TOO_LONG'; // 00 + 15 digits
  return 'TOO_SHORT';
}

export const PostponeCallDialog: FC<PostponeCallDialogProps> = ({ open, onClose, onConfirm }) => {
  const intl = useIntl();
  const { control, handleSubmit, reset, watch } = useForm<PostponeCallFormValues>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });
  const appointmentTimeValue = watch('appointmentTime');

  const resetForm = useCallback(() => {
    reset(defaultValues);
  }, [reset]);

  const handleDialogClose = useCallback<NonNullable<DialogProps['onClose']>>(
    (_, reason) => {
      if (reason === 'backdropClick') return;
      resetForm();
      onClose();
    },
    [onClose, resetForm]
  );

  const onSubmit = useCallback(
    async (data: PostponeCallFormValues) => {
      if (!data.appointmentTime) return;
      await onConfirm({
        appointmentTime: data.appointmentTime,
        appointmentName: data.appointmentName,
        appointmentTelephoneNumber: data.appointmentTelephoneNumber,
      });
      resetForm();
      onClose();
    },
    [onConfirm, onClose, resetForm]
  );

  const closeAndReset = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  return (
    <Dialog open={open} maxWidth="xs" fullWidth onClose={handleDialogClose} sx={styles.root}>
      <DialogTitle sx={styles.header}>
        <Box>
          <FormattedMessage id="postponeCall.dialog.title" defaultMessage="Odložit volání na:" />
        </Box>
        <ButtonBase onClick={closeAndReset} disableRipple>
          <Icon path={mdiClose} size={1} />
        </ButtonBase>
      </DialogTitle>
      <DialogContent sx={styles.content}>
        <Controller
          name="appointmentTime"
          control={control}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={cs}>
              <DateTimePicker
                label={<FormattedMessage id="postponeCall.dialog.dateLabel" defaultMessage="AppointmentTime" />}
                value={field.value}
                onChange={field.onChange}
                minDateTime={new Date()}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    size: 'small',

                    fullWidth: true,
                    required: true,
                    InputLabelProps: { shrink: true },
                    sx: { mt: 1 },
                  },
                }}
              />
            </LocalizationProvider>
          )}
        />
        <Controller
          name="appointmentName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={<FormattedMessage id="postponeCall.dialog.appointmentName" defaultMessage="AppointmentName" />}
              variant="outlined"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
        <Controller
          name="appointmentTelephoneNumber"
          control={control}
          rules={{
            validate: (v) => {
              const code = validatePhoneInput(String(v ?? ''));
              if (!code) return true;

              const messageByCode: Record<PhoneValidationErrorCode, string> = {
                CONTAINS_LETTERS: intl.formatMessage({
                  id: 'postponeCall.dialog.phoneError.containsLetters',
                  defaultMessage: 'Telefonní číslo nesmí obsahovat písmena (ani diakritiku).',
                }),
                INVALID_CHARACTERS: intl.formatMessage({
                  id: 'postponeCall.dialog.phoneError.invalidCharacters',
                  defaultMessage: 'Telefonní číslo obsahuje nepovolené znaky. Použijte jen číslice, mezery, +, -, ( ).',
                }),
                MISSING_COUNTRY_PREFIX: intl.formatMessage({
                  id: 'postponeCall.dialog.phoneError.missingCountryPrefix',
                  defaultMessage:
                    'Zadejte buď 9 číslic (např. 123 456 789), nebo číslo s předvolbou (např. +420 123 456 789 / 00420123456789).',
                }),
                TOO_SHORT: intl.formatMessage({
                  id: 'postponeCall.dialog.phoneError.tooShort',
                  defaultMessage: 'Telefonní číslo je příliš krátké.',
                }),
                TOO_LONG: intl.formatMessage({
                  id: 'postponeCall.dialog.phoneError.tooLong',
                  defaultMessage: 'Telefonní číslo je příliš dlouhé.',
                }),
              };

              return messageByCode[code];
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={
                <FormattedMessage
                  id="postponeCall.dialog.appointmentPhone"
                  defaultMessage="AppointmentTelephoneNumber"
                />
              }
              fullWidth
              error={Boolean(fieldState.error?.message)}
              helperText={fieldState.error?.message}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          )}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" onClick={closeAndReset}>
            <FormattedMessage id="postponeCall.dialog.cancel" defaultMessage="Zrušit" />
          </Button>
          <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={!appointmentTimeValue}>
            <FormattedMessage id="postponeCall.dialog.confirm" defaultMessage="Potvrdit" />
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
